import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, DownloadSimple, ShareNetwork, PaperPlaneTilt, CurrencyDollar, CheckCircle, EnvelopeSimple, ChatText, Sparkle, FloppyDisk, Eye, PencilSimple, Prohibit, Plus, Trash, StackSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, SHARE_PDF_URL, FILE_URL, downloadAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ResearchDrawer } from "@/components/ResearchDrawer";

const MODES = [["simple", "Simple"], ["standard", "Standard"], ["detailed", "Detailed"]];
const STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700", ready: "bg-indigo-100 text-indigo-700",
  sent: "bg-blue-100 text-blue-700", viewed: "bg-sky-100 text-sky-700",
  partially_paid: "bg-teal-100 text-teal-700", paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700", void: "bg-zinc-200 text-zinc-500",
};

function computeTotals(inv) {
  const li = (inv.line_items || []).reduce((s, x) => s + Number(x.amount || 0), 0);
  const mt = (inv.materials || []).filter((m) => !m.customer_supplied).reduce((s, m) => s + Number(m.amount || 0), 0);
  const subtotal = li + mt;
  const discount = Number(inv.discount || 0);
  const taxable = Math.max(subtotal - discount, 0);
  const tax = taxable * Number(inv.tax_rate || 0) / 100;
  const total = taxable + tax;
  const balance = total - Number(inv.amount_paid || 0);
  return { subtotal, discount, tax, total, balance };
}

export default function InvoicePreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [biz, setBiz] = useState({});
  const [cfg, setCfg] = useState({ sms_enabled: false, email_enabled: true });
  const [preview, setPreview] = useState(false);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [sendOpen, setSendOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [researchItem, setResearchItem] = useState(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [recurOpen, setRecurOpen] = useState(false);
  const [recur, setRecur] = useState({ frequency: "monthly", custom_days: 30, start_date: "", end_date: "", max_occurrences: 0 });

  const load = () => api.get(`/invoices/${id}`).then((r) => {
    setInv(r.data);
    const t = computeTotals(r.data);
    setPayAmt(String(t.balance.toFixed(2)));
    setEmailTo(r.data.customer_email || "");
    setSmsTo(r.data.customer_phone || "");
  });
  useEffect(() => { load(); api.get("/settings").then((r) => setBiz(r.data)); api.get("/config").then((r) => setCfg(r.data)); }, [id]);

  if (!inv) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const t = computeTotals(inv);
  const shareUrl = `${window.location.origin}/share/${inv.share_id}`;
  const locked = inv.status === "void";
  const editable = (inv.status === "draft" || inv.status === "ready") && !preview && !locked;
  const mode = inv.presentation_mode || "standard";

  const patch = (p) => setInv((v) => ({ ...v, ...p }));
  const setLine = (i, p) => {
    const line_items = [...inv.line_items];
    const li = { ...line_items[i], ...p };
    li.amount = +(Number(li.quantity || 1) * Number(li.unit_price || 0)).toFixed(2);
    line_items[i] = li;
    patch({ line_items });
  };
  const setMat = (i, p) => {
    const materials = [...inv.materials];
    materials[i] = { ...materials[i], ...p };
    patch({ materials });
  };
  const addLine = () => patch({ line_items: [...inv.line_items, { description: "New item", quantity: 1, unit_price: 0, amount: 0 }] });
  const rmLine = (i) => { patch({ line_items: inv.line_items.filter((_, idx) => idx !== i) }); setSel(sel.filter((x) => x !== i)); };
  const toggleSel = (i) => setSel(sel.includes(i) ? sel.filter((x) => x !== i) : [...sel, i]);
  const combine = () => {
    if (sel.length < 2) return;
    const chosen = sel.slice().sort((a, b) => a - b);
    const merged = chosen.map((i) => inv.line_items[i]);
    const amount = +merged.reduce((s, m) => s + Number(m.amount || 0), 0).toFixed(2);
    const desc = merged.map((m) => m.description).filter(Boolean).join(", ");
    const combinedLine = { description: desc, quantity: 1, unit_price: amount, amount };
    const rest = inv.line_items.filter((_, idx) => !chosen.includes(idx));
    const insertAt = chosen[0];
    rest.splice(insertAt, 0, combinedLine);
    patch({ line_items: rest });
    setSel([]);
    toast.success("Items combined");
  };

  const saveInv = async (next, silent) => {
    const body = next || inv;
    try {
      const r = await api.put(`/invoices/${id}`, body);
      setInv(r.data);
      if (!silent) toast.success("Draft saved");
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); return null; }
  };
  const saveDraft = async () => { setBusy("save"); await saveInv(inv); setBusy(""); };
  const changeMode = async (m) => { const next = { ...inv, presentation_mode: m }; setInv(next); if (!locked) await saveInv(next, true); };
  const polish = async () => {
    setBusy("polish");
    try {
      const saved = await saveInv(inv, true);
      if (!saved) { setBusy(""); return; }
      const r = await api.post(`/invoices/${id}/polish`); setInv(r.data); toast.success("Descriptions polished");
    } catch (e) { toast.error(e.response?.data?.detail || "Polish failed"); }
    setBusy("");
  };
  const setStatus = async (status) => { await api.post(`/invoices/${id}/status`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); load(); };
  const markReady = async () => { await saveInv(inv, true); setStatus("ready"); };
  const voidInvoice = async () => { setVoidOpen(false); await setStatus("void"); };
  const createRecurring = async () => {
    try {
      await api.post(`/recurring/from-invoice/${id}`, {
        frequency: recur.frequency,
        custom_days: recur.frequency === "custom" ? recur.custom_days : 0,
        start_date: recur.start_date,
        end_date: recur.end_date,
        max_occurrences: recur.max_occurrences,
        presentation_mode: inv.presentation_mode,
      });
      setRecurOpen(false);
      toast.success("Recurring service scheduled");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule recurring"); }
  };
  const download = () => downloadAuthed(`/invoices/${id}/pdf`, `${inv.invoice_number || "invoice"}.pdf`).catch(() => toast.error("Couldn't download the PDF."));
  const copyShare = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); };
  const recordPay = async () => {
    await api.post(`/invoices/${id}/pay`, { amount: Number(payAmt), method: payMethod });
    setPayOpen(false); toast.success("Payment recorded"); load();
  };
  const markPaid = () => {
    if (t.balance <= 0.01) { toast.info("Already paid in full"); return; }
    setPayAmt(String(t.balance.toFixed(2)));
    setPayOpen(true);
  };
  const sendEmail = async () => {
    setBusy("email");
    try { await api.post(`/invoices/${id}/email`, { email: emailTo, share_url: shareUrl, pdf_url: SHARE_PDF_URL(inv.share_id) }); setSendOpen(false); toast.success(`Emailed to ${emailTo}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Email failed. Please check the address and try again."); }
    finally { setBusy(""); }
  };
  const sendSms = async () => {
    try { await api.post(`/invoices/${id}/sms`, { phone: smsTo, share_url: shareUrl }); setSendOpen(false); toast.success(`Texted to ${smsTo}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "SMS failed"); }
  };

  const billMats = (inv.materials || []).filter((m) => !m.customer_supplied);
  const suppliedMats = (inv.materials || []).filter((m) => m.customer_supplied);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => nav("/history")} className="text-zinc-500"><ArrowLeft size={24} /></button>
        <h1 className="font-heading text-xl font-black tracking-tight">Invoice</h1>
        <span data-testid="invoice-status" className={`ml-auto text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${STATUS_COLORS[inv.status] || "bg-zinc-100 text-zinc-600"}`}>{(inv.status || "").replace("_", " ")}</span>
      </header>

      <div className="p-4 space-y-4">
        {/* Presentation mode */}
        <div className="flex gap-1 bg-zinc-100 rounded-xl p-1" data-testid="mode-selector">
          {MODES.map(([m, label]) => (
            <button key={m} data-testid={`mode-${m}`} onClick={() => changeMode(m)}
              className={`flex-1 h-9 rounded-lg text-sm font-bold transition-colors ${mode === m ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}>{label}</button>
          ))}
        </div>

        {/* Receipt */}
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm" data-testid="invoice-receipt">
          <div className="bg-zinc-900 text-white p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-heading font-black text-xl">{biz.business_name || "My Service"}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{[biz.phone, biz.email].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="text-right">
                <p className="font-heading font-black text-orange-500 text-lg">INVOICE</p>
                <p className="text-xs text-zinc-400">{inv.invoice_number}</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="flex justify-between text-sm mb-4">
              <div>
                <p className="text-[10px] uppercase font-bold text-zinc-400">Bill To</p>
                <p className="font-semibold text-zinc-900">{inv.customer_name || "—"}</p>
                <p className="text-zinc-500 text-xs">{inv.location}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase font-bold text-zinc-400">Date</p>
                <p className="font-semibold text-zinc-900">{inv.date}</p>
                {editable ? (
                  <div className="mt-1">
                    <label className="text-[10px] uppercase font-bold text-zinc-400 block">Due</label>
                    <Input data-testid="due-date-input" type="date" value={inv.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} className="h-9 text-sm" />
                  </div>
                ) : inv.due_date && <p className="text-xs text-zinc-500 mt-1">Due {inv.due_date}</p>}
              </div>
            </div>

            {/* Line items */}
            {mode === "simple" ? (
              <div className="space-y-2.5 border-t border-zinc-100 pt-3">
                <Row label="Labor & Services" value={money(inv.line_items.reduce((s, x) => s + Number(x.amount || 0), 0))} bold />
                {billMats.length > 0 && <Row label="Materials & Supplies" value={money(billMats.reduce((s, m) => s + Number(m.amount || 0), 0))} bold />}
              </div>
            ) : (
              <div className="space-y-2.5 border-t border-zinc-100 pt-3">
                {inv.line_items.map((li, i) => (
                  <div key={i} className="text-sm" data-testid={`inv-line-${i}`}>
                    {editable ? (
                      <div className="border border-zinc-200 rounded-lg p-2.5 space-y-2">
                        <div className="flex items-start gap-2">
                          <input type="checkbox" data-testid={`inv-sel-${i}`} checked={sel.includes(i)} onChange={() => toggleSel(i)} className="mt-2 accent-orange-600" />
                          <Textarea data-testid={`inv-desc-${i}`} value={li.description} onChange={(e) => setLine(i, { description: e.target.value })} rows={2} className="flex-1 resize-none text-sm" />
                          <button data-testid={`inv-del-${i}`} onClick={() => rmLine(i)} className="text-zinc-300 hover:text-red-500 pt-1"><Trash size={18} /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <MiniField label="Qty"><Input data-testid={`inv-qty-${i}`} type="number" value={li.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} className="h-9" /></MiniField>
                          <MiniField label="Rate ($)"><Input data-testid={`inv-rate-${i}`} type="number" value={li.unit_price} onChange={(e) => setLine(i, { unit_price: Number(e.target.value) })} className="h-9" /></MiniField>
                          <MiniField label="Amount"><div className="h-9 flex items-center font-mono-num font-semibold text-zinc-900" data-testid={`inv-amt-${i}`}>{money(li.amount)}</div></MiniField>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-zinc-800">{li.description}</p>
                          {(mode === "detailed" && li.quantity > 1) && <p className="text-xs text-zinc-400">{li.quantity} × {money(li.unit_price)}</p>}
                          {li.research?.sources?.length > 0 && !preview && (
                            <button data-testid={`inv-research-${i}`} onClick={() => setResearchItem(li)} className="text-[11px] font-semibold text-orange-600 underline underline-offset-2">View Pricing Research</button>
                          )}
                        </div>
                        <p className="font-mono-num font-semibold text-zinc-900 shrink-0">{money(li.amount)}</p>
                      </div>
                    )}
                  </div>
                ))}
                {billMats.map((m, i) => (
                  <div key={`m${i}`} className="flex justify-between gap-3 text-sm" data-testid={`inv-mat-${i}`}>
                    <p className="text-zinc-800 min-w-0">
                      {m.name} <span className="text-xs text-zinc-400">(materials{mode === "detailed" && [m.brand, m.model_sku].filter(Boolean).length ? ` · ${[m.brand, m.model_sku].filter(Boolean).join(" ")}` : ""})</span>
                    </p>
                    <p className="font-mono-num font-semibold text-zinc-900 shrink-0">{money(m.amount)}</p>
                  </div>
                ))}
                {suppliedMats.length > 0 && mode === "detailed" && (
                  <p className="text-[11px] text-zinc-400">Customer-supplied (no charge): {suppliedMats.map((m) => m.name).join(", ")}</p>
                )}
              </div>
            )}

            {editable && mode !== "simple" && (
              <div className="flex gap-2 mt-3">
                <button data-testid="add-line-btn" onClick={addLine} className="flex-1 border border-dashed border-zinc-300 rounded-lg py-2 text-sm font-semibold text-zinc-500 flex items-center justify-center gap-1.5"><Plus size={16} weight="bold" /> Add item</button>
                {sel.length >= 2 && <button data-testid="combine-btn" onClick={combine} className="flex-1 border border-orange-300 bg-orange-50 rounded-lg py-2 text-sm font-bold text-orange-700 flex items-center justify-center gap-1.5"><StackSimple size={16} weight="bold" /> Combine {sel.length}</button>}
              </div>
            )}

            {/* Totals */}
            <div className="border-t border-zinc-100 mt-4 pt-3 space-y-1.5">
              <Row label="Subtotal" value={money(t.subtotal)} />
              {editable ? (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-zinc-500">Discount ($)</span>
                  <Input data-testid="discount-input" type="number" value={inv.discount || 0} onChange={(e) => patch({ discount: Number(e.target.value) })} className="h-9 w-28 text-right" />
                </div>
              ) : (t.discount > 0 && <Row label="Discount" value={`- ${money(t.discount)}`} />)}
              {editable && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-zinc-500">Deposit request ($)</span>
                  <Input data-testid="deposit-input" type="number" value={inv.deposit_amount || 0} onChange={(e) => patch({ deposit_amount: Number(e.target.value) })} className="h-9 w-28 text-right" />
                </div>
              )}
              {editable ? (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-zinc-500">Tax rate (%)</span>
                  <Input data-testid="tax-input" type="number" value={inv.tax_rate || 0} onChange={(e) => patch({ tax_rate: Number(e.target.value) })} className="h-9 w-28 text-right" />
                </div>
              ) : (t.tax > 0 && <Row label={`Tax (${inv.tax_rate}%)`} value={money(t.tax)} />)}
              {inv.amount_paid > 0 && <Row label="Paid" value={`- ${money(inv.amount_paid)}`} />}
              <div className="flex justify-between items-center pt-2">
                <span className="font-heading font-bold text-zinc-900">{inv.amount_paid > 0 ? "BALANCE DUE" : "TOTAL DUE"}</span>
                <span className="font-heading font-black text-2xl text-orange-600" data-testid="invoice-total">{money(t.balance)}</span>
              </div>
            </div>

            {/* Notes */}
            {editable ? (
              <div className="mt-4 border-t border-zinc-100 pt-3">
                <label className="text-[10px] uppercase font-bold text-zinc-400">Note to customer</label>
                <Textarea data-testid="invoice-notes" value={inv.notes || ""} onChange={(e) => patch({ notes: e.target.value })} rows={2} className="mt-1 text-sm" />
              </div>
            ) : (inv.notes && <p className="text-xs text-zinc-500 mt-4 border-t border-zinc-100 pt-3">{inv.notes}</p>)}
          </div>
        </div>

        {/* Owner profit summary */}
        {inv.profit !== undefined && !preview && (
          <div className="bg-zinc-900 text-white rounded-2xl p-4" data-testid="profit-summary">
            <p className="text-[10px] uppercase font-bold text-zinc-400 mb-2">Owner summary · not shown to customer</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-[10px] text-zinc-400">Material cost</p><p className="font-heading font-bold" data-testid="profit-material-cost">{money(inv.material_cost)}</p></div>
              <div><p className="text-[10px] text-zinc-400">Expenses</p><p className="font-heading font-bold">{money(inv.expense_cost)}</p></div>
              <div><p className="text-[10px] text-zinc-400">Profit</p><p className="font-heading font-black text-green-400" data-testid="profit-value">{money(t.subtotal - t.discount - Number(inv.material_cost || 0) - Number(inv.expense_cost || 0))}</p></div>
            </div>
          </div>
        )}

        {/* Edit tools */}
        {(inv.status === "draft" || inv.status === "ready") && !locked && (
          <div className="grid grid-cols-3 gap-3">
            <Action onClick={saveDraft} icon={FloppyDisk} label={busy === "save" ? "Saving…" : "Save Draft"} testid="save-draft-btn" disabled={busy === "save"} />
            <Action onClick={polish} icon={Sparkle} label={busy === "polish" ? "Polishing…" : "AI Polish"} testid="polish-btn" disabled={busy === "polish"} />
            <Action onClick={() => setPreview((p) => !p)} icon={preview ? PencilSimple : Eye} label={preview ? "Edit" : "Preview"} testid="preview-toggle-btn" />
          </div>
        )}

        {/* Primary actions */}
        <div className="grid grid-cols-2 gap-3">
          <Action onClick={download} icon={DownloadSimple} label="Generate PDF" testid="download-pdf-btn" />
          <Action onClick={copyShare} icon={ShareNetwork} label="Copy Link" testid="share-link-btn" />
          {inv.status === "draft" && <Action onClick={markReady} icon={CheckCircle} label="Mark Ready" testid="mark-ready-btn" />}
          {!locked && inv.status !== "paid" && <Action onClick={() => setSendOpen(true)} icon={PaperPlaneTilt} label="Send Invoice" testid="send-btn" />}
          {!locked && inv.status !== "paid" && <Action onClick={() => setPayOpen(true)} icon={CurrencyDollar} label="Record Payment" testid="record-payment-btn" />}
          {!locked && inv.status !== "paid" && <Action onClick={markPaid} icon={CheckCircle} label="Mark Paid" testid="mark-paid-btn" primary />}
          {!locked && inv.status !== "paid" && <Action onClick={() => setVoidOpen(true)} icon={Prohibit} label="Void" testid="void-btn" />}
          {!locked && <Action onClick={() => setRecurOpen(true)} icon={ArrowsClockwise} label="Recurring" testid="make-recurring-btn" />}
        </div>
        {locked && <p className="text-center text-sm font-bold text-zinc-400" data-testid="void-notice">This invoice is VOID and read-only.</p>}
      </div>

      {/* Payment drawer */}
      <Drawer open={payOpen} onOpenChange={setPayOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Record Payment</DrawerTitle></DrawerHeader>
          <div className="px-4 space-y-3">
            <div>
              <label className="text-[11px] uppercase font-bold text-zinc-400">Amount received</label>
              <Input data-testid="payment-amount" type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="h-14 text-2xl font-heading font-black mt-1" />
            </div>
            <div>
              <label className="text-[11px] uppercase font-bold text-zinc-400">Method</label>
              <select data-testid="payment-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="h-11 w-full border border-zinc-200 rounded-lg px-2 bg-white mt-1 capitalize">
                {["cash", "check", "card", "bank transfer", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <DrawerFooter><Button data-testid="confirm-payment-btn" onClick={recordPay} className="h-12 text-base font-bold">Confirm Payment</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Send drawer */}
      <Drawer open={sendOpen} onOpenChange={setSendOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Send Invoice</DrawerTitle></DrawerHeader>
          <div className="px-4 space-y-4 pb-4">
            <div>
              <label className="text-[11px] uppercase font-bold text-zinc-400">Email invoice</label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="email-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="name@example.com" className="h-11 flex-1" />
                <Button data-testid="send-email-btn" onClick={sendEmail} disabled={!emailTo} className="h-11 font-bold gap-1.5"><EnvelopeSimple size={18} weight="bold" /> Email</Button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase font-bold text-zinc-400">Text invoice {cfg.sms_enabled ? "" : "(add Twilio to enable)"}</label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="sms-input" type="tel" value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="+14155551234" className="h-11 flex-1" disabled={!cfg.sms_enabled} />
                <Button data-testid="send-sms-btn" onClick={sendSms} disabled={!smsTo || !cfg.sms_enabled} variant="outline" className="h-11 font-bold gap-1.5"><ChatText size={18} weight="bold" /> Text</Button>
              </div>
            </div>
            <Button data-testid="send-copy-link-btn" onClick={copyShare} variant="outline" className="w-full h-11 font-bold gap-1.5"><ShareNetwork size={18} weight="bold" /> Copy Share Link</Button>
          </div>
        </DrawerContent>
      </Drawer>

      <ResearchDrawer item={researchItem} onClose={() => setResearchItem(null)} />

      <Drawer open={recurOpen} onOpenChange={setRecurOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Repeat this service</DrawerTitle></DrawerHeader>
          <div className="px-4 space-y-3">
            <div>
              <label className="text-[11px] uppercase font-bold text-zinc-400">Frequency</label>
              <select data-testid="recur-frequency" value={recur.frequency} onChange={(e) => setRecur({ ...recur, frequency: e.target.value })} className="h-11 w-full border border-zinc-200 rounded-lg px-2 bg-white mt-1">
                {[["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["monthly", "Monthly"], ["bimonthly", "Every 2 months"], ["quarterly", "Quarterly"], ["semiannual", "Every 6 months"], ["annual", "Annually"], ["custom", "Custom (days)"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {recur.frequency === "custom" && (
              <div><label className="text-[11px] uppercase font-bold text-zinc-400">Every N days</label>
                <Input data-testid="recur-custom-days" type="number" value={recur.custom_days} onChange={(e) => setRecur({ ...recur, custom_days: Number(e.target.value) })} className="h-11 mt-1" /></div>
            )}
            <div><label className="text-[11px] uppercase font-bold text-zinc-400">Start date</label>
              <Input data-testid="recur-start" type="date" value={recur.start_date} onChange={(e) => setRecur({ ...recur, start_date: e.target.value })} className="h-11 mt-1" /></div>
            <div><label className="text-[11px] uppercase font-bold text-zinc-400">End date (optional)</label>
              <Input data-testid="recur-end" type="date" value={recur.end_date} onChange={(e) => setRecur({ ...recur, end_date: e.target.value })} className="h-11 mt-1" /></div>
            <div><label className="text-[11px] uppercase font-bold text-zinc-400">Max occurrences (0 = no limit)</label>
              <Input data-testid="recur-max" type="number" value={recur.max_occurrences} onChange={(e) => setRecur({ ...recur, max_occurrences: Number(e.target.value) })} className="h-11 mt-1" /></div>
            <p className="text-xs text-zinc-400">Each occurrence creates a DRAFT invoice you review before sending. Editing this schedule never changes past invoices.</p>
          </div>
          <DrawerFooter><Button data-testid="recur-create-btn" onClick={createRecurring} className="h-12 font-bold">Schedule Recurring</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>        <AlertDialogContent data-testid="void-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Void this invoice?</AlertDialogTitle>
            <AlertDialogDescription>Voiding marks {inv.invoice_number} as cancelled and makes it read-only. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="void-cancel-btn">Keep invoice</AlertDialogCancel>
            <AlertDialogAction data-testid="void-confirm-btn" onClick={voidInvoice} className="bg-red-600 hover:bg-red-700">Void invoice</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between text-sm ${bold ? "text-zinc-800" : "text-zinc-500"}`}><span>{label}</span><span className="font-mono-num">{value}</span></div>
);
const MiniField = ({ label, children }) => (
  <div><label className="text-[10px] uppercase font-bold text-zinc-400 block mb-1">{label}</label>{children}</div>
);
const Action = ({ onClick, icon: Icon, label, testid, disabled, primary }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`h-16 rounded-xl border flex flex-col items-center justify-center gap-1 font-semibold text-sm active:translate-y-0.5 transition-transform disabled:opacity-40 ${primary ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-800 border-zinc-200"}`}>
    <Icon size={22} weight="bold" /> {label}
  </button>
);
